import { useEffect, useRef, useState } from "react";
import { FONT_FACES, useDisplay } from "../lib/useDisplay";

// "Aa" in the top bar, opening a small panel with the two reading controls:
// size (a − / + pair, like a browser's zoom) and typeface. Both apply
// instantly and persist, so the setting follows you from page to page and
// across launches.
//
// It sits beside the theme toggle because it belongs to the same family of
// choices — how the page looks to you, not what's in it — and because the top
// bar is the one piece of chrome present on every screen, including the ones
// you'd be reading from while cooking.
export function DisplayControls({ className }: { className?: string }) {
  const { scale, font, setScale, setFont, stepScale, reset, atMin, atMax } = useDisplay();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close on an outside click or Escape — a menu you can only dismiss by
  // hitting the same small button again is a trap on a phone.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pct = Math.round(scale * 100);

  return (
    <div className={"display-controls" + (className ? " " + className : "")} ref={wrapRef}>
      <button
        type="button"
        className={"display-trigger" + (open ? " on" : "")}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Text size and typeface"
        aria-label="Text size and typeface"
      >
        <span className="display-aa" aria-hidden="true">
          Aa
        </span>
      </button>

      {open && (
        <div className="display-panel" role="dialog" aria-label="Reading">
          <div className="display-row">
            <span className="display-label">Text size</span>
            <span className="display-pct">{pct}%</span>
          </div>
          <div className="display-stepper">
            <button
              type="button"
              onClick={() => stepScale(-1)}
              disabled={atMin}
              aria-label="Smaller text"
              title="Smaller text"
            >
              A<span className="display-sign">−</span>
            </button>
            <button
              type="button"
              className="display-step-reset"
              onClick={() => setScale(1)}
              aria-label="Reset text size to 100%"
              title="Reset text size"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={() => stepScale(1)}
              disabled={atMax}
              aria-label="Larger text"
              title="Larger text"
            >
              A<span className="display-sign">+</span>
            </button>
          </div>

          <div className="display-row display-row-top">
            <span className="display-label">Typeface</span>
          </div>
          <div className="display-faces">
            {FONT_FACES.map((f) => (
              <button
                key={f.id}
                type="button"
                className={"display-face face-" + f.id + (font === f.id ? " on" : "")}
                aria-pressed={font === f.id}
                aria-label={f.label + " typeface — " + f.note}
                onClick={() => setFont(f.id)}
              >
                <span className="display-face-name">{f.label}</span>
                <span className="display-face-note">{f.note}</span>
              </button>
            ))}
          </div>

          <button type="button" className="display-reset" onClick={reset}>
            Back to defaults
          </button>
        </div>
      )}

      <DisplayControlsStyles />
    </div>
  );
}

function DisplayControlsStyles() {
  return (
    <style>{`
      /* Everything below is written as .display-controls X / .display-panel X
         rather than bare class selectors: the control lives inside .appbar-nav,
         whose blanket "every button in here is a small uppercase nav link" rule
         is a single class + element, and would otherwise win. */
      .display-controls { position: relative; display: inline-flex; align-items: center; }
      .display-controls .display-trigger {
        display: inline-flex; align-items: center; justify-content: center;
        background: none; border: 0; padding: 0; color: var(--fg-muted);
        cursor: pointer; transition: var(--t-fast) var(--ease);
      }
      .display-controls .display-trigger:hover,
      .display-controls .display-trigger.on { color: var(--fg); }
      .display-controls .display-aa {
        font-family: var(--font-serif); font-weight: 700; font-size: 15px;
        letter-spacing: 0; text-transform: none; line-height: 1;
        /* It's a button that happens to be made of letters — a tap that
           highlights them like body copy looks broken. */
        user-select: none; -webkit-user-select: none;
      }
      .display-controls .display-trigger { -webkit-tap-highlight-color: transparent; }

      .display-panel {
        position: absolute; top: calc(100% + 14px); right: 0; z-index: 60;
        width: 244px; padding: 14px 16px 12px;
        background: var(--bg); border: 1px solid var(--line);
        box-shadow: 0 18px 44px rgba(0, 0, 0, 0.22);
        text-align: left; cursor: default;
      }
      .display-panel .display-row {
        display: flex; align-items: baseline; justify-content: space-between;
      }
      .display-panel .display-row-top {
        margin-top: 16px; padding-top: 14px; border-top: 1px solid var(--line);
      }
      .display-panel .display-label {
        font-family: var(--font-sans); font-size: 9px; font-weight: 500;
        letter-spacing: 2.5px; text-transform: uppercase; color: var(--fg-muted);
      }
      .display-panel .display-pct {
        font-family: var(--font-mono); font-size: 11px; color: var(--accent);
        letter-spacing: 0;
      }

      .display-panel .display-stepper {
        display: flex; align-items: stretch; gap: 6px; margin-top: 10px;
      }
      .display-panel .display-stepper button {
        flex: 1; padding: 9px 0;
        background: var(--surface); border: 1px solid var(--line); color: var(--fg);
        font-family: var(--font-serif); font-weight: 700; font-size: 15px;
        letter-spacing: 0; text-transform: none;
        cursor: pointer; transition: var(--t-fast) var(--ease);
      }
      .display-panel .display-stepper button:hover:not(:disabled) {
        border-color: var(--accent); color: var(--accent);
      }
      .display-panel .display-stepper button:disabled { opacity: 0.4; cursor: default; }
      .display-panel .display-sign {
        font-family: var(--font-sans); font-size: 12px; margin-left: 1px;
      }
      .display-panel .display-stepper .display-step-reset {
        flex: 1.3;
        font-family: var(--font-sans); font-weight: 500; font-size: 9px;
        letter-spacing: 1.6px; text-transform: uppercase; color: var(--fg-muted);
      }

      .display-panel .display-faces {
        display: flex; flex-direction: column; gap: 6px; margin-top: 10px;
      }
      .display-panel .display-face {
        display: flex; flex-direction: column; gap: 2px; width: 100%; text-align: left;
        background: var(--surface); border: 1px solid var(--line); color: var(--fg);
        padding: 8px 10px; cursor: pointer; transition: var(--t-fast) var(--ease);
        letter-spacing: 0; text-transform: none;
      }
      .display-panel .display-face:hover { border-color: var(--accent); }
      .display-panel .display-face.on { border-color: var(--accent); background: var(--accent-dim); }
      .display-panel .display-face-name { font-size: 15px; font-weight: 700; line-height: 1.2; }
      .display-panel .display-face-note {
        font-size: 11.5px; color: var(--fg-muted); font-style: italic; line-height: 1.3;
      }
      /* Each option previews itself: the label is set in the face it selects,
         which is the only honest way to choose a typeface. Named literally
         rather than through the --font-* tokens, which are what's being
         chosen — a preview that changed with the current setting would show
         you three copies of the face you already have. */
      .display-panel .face-editorial .display-face-name {
        font-family: 'Playfair Display', Georgia, serif;
      }
      .display-panel .face-editorial .display-face-note {
        font-family: 'Iowan Old Style', Charter, Georgia, serif;
      }
      .display-panel .face-modern .display-face-name,
      .display-panel .face-modern .display-face-note {
        font-family: 'Inter', system-ui, sans-serif; font-style: normal;
      }
      .display-panel .face-classic .display-face-name,
      .display-panel .face-classic .display-face-note {
        font-family: 'Iowan Old Style', Charter, Georgia, 'Times New Roman', serif;
      }

      .display-panel .display-reset {
        display: block; width: 100%; margin-top: 12px; padding: 8px 0 0;
        background: none; border: 0; border-top: 1px solid var(--line);
        color: var(--fg-muted); cursor: pointer;
        font-family: var(--font-sans); font-size: 9px; font-weight: 500;
        letter-spacing: 2px; text-transform: uppercase;
        transition: var(--t-fast) var(--ease);
      }
      .display-panel .display-reset:hover { color: var(--accent); }

      /* Phones: the nav (and the trigger inside it) is hidden, so the
         standalone one in the top bar's trailing slot takes over — same 44px
         tap target as the theme toggle it sits next to. */
      .display-controls-mobile { display: none; }
      @media (max-width: 900px) {
        .appbar-nav .display-controls { display: none; }
        .display-controls-mobile { display: inline-flex; }
        .display-controls-mobile .display-trigger { padding: 10px; margin: -10px 0; }
        .display-controls-mobile .display-aa { font-size: 17px; }
        .display-panel { top: calc(100% + 10px); }
      }
    `}</style>
  );
}
